/**
 * ============================================================
 * CONTRACT 8 — Community Feedback ("found useful" votes)
 * ============================================================
 *
 * Owner:     the feedback API (issues #617 vote mechanic, #618 reason capture)
 * Consumers: the web frontend — catalog cards, plugin detail page, My Skills (#621)
 *            the Recommended sort (#619) reads the tally and nothing else
 *
 * Validated interaction design lives in the prototype
 * (Delivery-Platform/product-management, poc-site/public/pocs/ai-marketplace-community).
 * The prototype is a BEHAVIOUR SPEC, not a source to adapt: it is one hand-written
 * HTML/JS file built from interpolated template strings with inline `onclick`
 * handlers and inline styles, all three of which this repo forbids in new web code.
 *
 * Why an API, and not a sibling artifact like Contract 7
 * ─────────────────────────────────────────────────────
 * Adoption (contract-7-adoption.ts) is published as a static artifact because its
 * data arrives from an upstream mart on a 3–4 day lag — freshness decoupling was
 * the whole point. Votes are the opposite: they are written by the user standing
 * in front of you and must be reflected immediately. So the write path is a live
 * authenticated endpoint, and the voter's own UI updates from the write's response
 * rather than waiting for a republish.
 *
 * `TalliesResponse` still carries a `schemaVersion`, so if read volume ever
 * justifies snapshotting tallies to S3 and merging them at read time (the
 * Contract 7 pattern), that migration is drop-in rather than a reshape.
 *
 * Keyed by `MarketplaceEntry.installName`
 * ──────────────────────────────────────
 * Same key as Contract 7, for the same reason: installName is the unique
 * per-entry identifier and the catalog builder enforces it (buildCatalog ends in
 * dedupeByInstallName, because "the published catalog must never carry duplicate
 * ids"). Consumers look up `entry.installName` directly and reconstruct NOTHING.
 *
 * Do NOT key by officialIdentityKey() — it collapses a plugin's synthesized
 * per-skill entries onto their parent (measured 2026-08: 339 distinct keys for
 * 1532 entries; `observability` alone collapses 198 onto one). Voting through
 * that key would pool a whole family's votes into a single tally.
 *
 * An entry with no installName is unvotable. Hide the affordance; never error.
 *
 * Grain: ONE VOTE PER ENTRY, no rollup (product decision, 2026-08-10)
 * ──────────────────────────────────────────────────────────────────
 * Every catalog entry gets its own independent tally — a plugin and each of the
 * per-skill entries synthesized from its `skills[]` are separate, votable things.
 *
 * This deliberately DIFFERS from adoption, which unions distinct users across a
 * plugin's own name plus every name in its `skills[]`. The asymmetry is intended:
 * usage rollup is a factual claim (someone who ran your skill did run your
 * plugin), whereas a vote rollup would assert an OPINION the voter never
 * expressed — they said the linter was useful, not that the whole plugin was.
 *
 * Consequence to expect: a plugin entry can read "Not enough votes yet" while its
 * skill entries all carry ratings. That is correct, not a bug.
 *
 * If bundling is revisited, rollup is a READ-TIME computation over data the
 * browser already holds — web/src/lib/parent-plugin.ts already builds the
 * child → parent index. Nothing stored changes and no migration is needed, which
 * is why this default was safe to adopt before the question was fully settled.
 *
 * Votes are mutable, with no time box (product decision, 2026-08-10)
 * ─────────────────────────────────────────────────────────────────
 * A voter may change or withdraw their vote at any time, indefinitely. This is
 * not a feature so much as the absence of a restriction: one-vote-per-user is
 * enforced by the storage key (one row per voter per entry), so a repeat vote is
 * a physical overwrite. LOCKING would have been the extra work — a conditional
 * rejection plus an error path plus a "you already voted" UI state the prototype
 * never designed.
 *
 * It also creates no abuse amplification: one row per voter means a
 * flip-flopper moves the tally by exactly ±1 however many times they toggle.
 *
 * The cost is arithmetic. See TALLY TRANSITIONS on `FeedbackTally` — a direction
 * switch must NOT increment `total`, and a resubmitted identical vote must be a
 * no-op. Both are easy to get wrong and both permanently corrupt a counter.
 *
 * Reason CODES, never labels
 * ──────────────────────────
 * #618's AC: "selected reason codes are stored as an array, not a single value".
 * The prototype stores display text (`["Saved me time"]`), which is fine for a
 * prototype and wrong here — rewording a chip would fork one meaning across two
 * strings and split every historical count, fixable only by rewriting stored
 * rows. Codes are the meaning; labels are cosmetics owned by the frontend.
 *
 * This union is APPEND-ONLY *from launch*. Retiring a chip removes it from the set
 * OFFERED to voters, never from the set that can be RENDERED — historical votes
 * still carry it, so the frontend's label map must resolve every code here, forever.
 *
 * The one exception has already been used: WELL_DOCUMENTED was REPLACED outright by
 * FIT_MY_USE_CASE on product sign-off (2026-08-11, too much overlap with
 * CLEAR_INSTRUCTIONS). That was a clean substitution rather than a retirement
 * because nothing was deployed and no vote existed yet — which is precisely why the
 * set was locked before launch rather than after. Once real votes exist that option
 * is gone: a swap then means rewriting stored rows.
 */

import type { MarketplaceEntry } from "./contract-3-marketplace-json";

// ── Tunables ───────────────────────────────────────────────

/**
 * Minimum total votes before a ratio is shown or ranked on.
 *
 * Below this, the UI shows "Not enough votes yet" instead of a percentage (#620)
 * and the Recommended sort substitutes a neutral 0.5 for the item's raw ratio
 * (#619). ONE constant with two consumers, deliberately: the prototype hardcoded
 * 5 in both places (`usefulnessInline` and `recommendedScore`), which is exactly
 * how a display gate and a ranking gate silently drift apart.
 */
export const MIN_VOTES_FOR_RATIO = 5;

/**
 * Maximum length of the free-text "Other" reason, in characters.
 *
 * MUST be enforced server-side on write. The prototype's `maxlength="140"` is a
 * courtesy that stops users typing into a wall — it is not a limit, because
 * anyone can POST past an HTML attribute. Client-side validation is UX;
 * server-side validation is the rule.
 */
export const MAX_REASON_TEXT_LENGTH = 140;

/**
 * Maximum catalog entries a single GET /feedback/received call (#621) fans out over.
 *
 * That endpoint reads one contributor's feedback across a plugin PLUS every skill
 * synthesized from its `skills[]`. Each entry is its own DynamoDB partition, so the
 * read is one Query per entry with no batch equivalent — a contributor's largest
 * plugin in prod carries 50 skills (51 entries). This caps that per-expand fan-out;
 * a response that hits the cap sets `truncated`, mirroring `TalliesResponse`.
 */
export const MAX_FAMILY_ENTRIES = 60;

// ── Vote direction ─────────────────────────────────────────

/**
 * Which thumb was pressed. "up" / "down" matches the prototype's vocabulary
 * end-to-end, so porting its behaviour introduces no translation layer.
 */
export type VoteDirection = "up" | "down";

// ── Reason codes ───────────────────────────────────────────

/** Presets offered after an UP vote. */
export type UsefulReasonCode =
  | "SAVED_TIME"
  | "CLEAR_INSTRUCTIONS"
  | "WORKED_AS_EXPECTED"
  | "FIT_MY_USE_CASE";

/** Presets offered after a DOWN vote. */
export type NotUsefulReasonCode =
  | "DID_NOT_WORK"
  | "UNCLEAR_INSTRUCTIONS"
  | "MISSING_SOMETHING"
  | "OUT_OF_DATE";

export type ReasonCode = UsefulReasonCode | NotUsefulReasonCode;

/**
 * Which codes are valid for which direction. The sets are symmetric by design —
 * the 7/30 planning review concluded it is "equally valuable to know why somebody
 * didn't find it useful".
 *
 * The server MUST validate submitted codes against the set for the submitted
 * direction. Without it a down-vote could carry FIT_MY_USE_CASE, and the
 * contributor's dashboard would show praise attached to a complaint.
 *
 * Note there is no `OTHER` code. "Other" is a UI affordance that reveals the free
 * text field; its content lands in `reasonText`, and the code array stays presets
 * only. A vote can therefore carry codes, text, both, or neither.
 */
export const REASON_CODES_BY_DIRECTION: {
  readonly up: readonly UsefulReasonCode[];
  readonly down: readonly NotUsefulReasonCode[];
} = {
  up: ["SAVED_TIME", "CLEAR_INSTRUCTIONS", "WORKED_AS_EXPECTED", "FIT_MY_USE_CASE"],
  down: ["DID_NOT_WORK", "UNCLEAR_INSTRUCTIONS", "MISSING_SOMETHING", "OUT_OF_DATE"],
};

// ── Stored vote ────────────────────────────────────────────

/**
 * One person's vote on one entry — the stored record.
 *
 * The voter's identity is NOT part of this shape. It lives in the storage key
 * (derived from the verified Entra token's `oid`, never from the request body) so
 * that one-vote-per-entry is structural. Keeping it out of the record is what
 * makes the anonymity promise in the UI — "Your response is counted anonymously"
 * — hard to break by accident: there is no identity field to leak.
 */
export interface FeedbackVote {
  direction: VoteDirection;

  /**
   * Selected presets. An array, per #618 — a voter picks any combination that
   * applies. Empty when the voter skipped, or supplied only free text.
   */
  reasonCodes: ReasonCode[];

  /**
   * Free-text "Other" reason, capped at MAX_REASON_TEXT_LENGTH. Null when absent.
   *
   * UNTRUSTED USER INPUT that a contributor later reads in their dashboard.
   * Store as data, render as text — never as HTML.
   */
  reasonText: string | null;

  /** ISO-8601. Overwritten on a changed vote; there is no v1 vote history. */
  votedAt: string;
}

// ── Public tally ───────────────────────────────────────────

/**
 * The public counts for one entry.
 *
 * Both numbers are required. `useful` alone is not enough: "N of M found useful"
 * needs M (#617), the "Not enough votes yet" gate needs M (#620), and #619's
 * MIN_VOTES_FOR_RATIO fallback needs M. Serving a pre-computed percentage instead
 * would make all three unimplementable.
 *
 * There is no `notUseful` field. It is derivable, and #617 requires that no
 * negative count is ever displayed — so it is not stored as a thing to render by
 * mistake. ("N of M" is the validated display; M is total votes, not a negative
 * tally.)
 *
 * TALLY TRANSITIONS — the arithmetic mutable votes require
 * ───────────────────────────────────────────────────────
 *   from   to            total   useful
 *   —      up             +1      +1
 *   —      down           +1       0
 *   up     down            0      -1     ← NOT +1: the voter already counted
 *   down   up              0      +1     ← NOT +1: same reason
 *   up     — (undo)       -1      -1
 *   down   — (undo)       -1       0
 *   up     up (resubmit)   0       0     ← idempotent: double-click, retry
 *
 * A write therefore cannot be a blind increment — it must know the prior state to
 * compute the delta, and should commit under a condition on that prior state so a
 * concurrent double-submit fails rather than double-counting.
 */
export interface FeedbackTally {
  /** Votes with direction "up". */
  useful: number;
  /** All votes cast, both directions. The M in "N of M found useful". */
  total: number;
}

// ── API: read tallies (bulk) ───────────────────────────────

/**
 * GET /feedback/tallies
 *
 * Requires an Entra Bearer token. Every visible catalog card needs a tally, so
 * this is bulk-by-design: one call per catalog load, not one per entry.
 *
 * Entries with no votes are ABSENT rather than present with zeroes, so an absent
 * key means "no votes yet" — the same single-empty-state discipline as Contract 7.
 * Expect this map to be sparse and the empty state to be the common case at
 * launch.
 */
export interface TalliesResponse {
  /**
   * 1.1 adds the optional `truncated` field. MINOR because it is additive: a 1.0
   * consumer reading a 1.1 response sees the same `entries` map and simply ignores a
   * field it does not know about.
   */
  schemaVersion: "1.1";

  /** ISO-8601. Present so a cached response can be reasoned about. */
  generatedAt: string;

  /** Tallies keyed by `MarketplaceEntry.installName`. Sparse. */
  entries: Record<string, FeedbackTally>;

  /**
   * Present and `true` ONLY when the server stopped reading before the tally index
   * was exhausted, so `entries` is a partial view. Absent on a complete response.
   *
   * This cannot be inferred from `entries`: an entry with no votes is legitimately
   * absent (absence is the single empty signal above), so a missing key never
   * distinguishes "nobody voted" from "the server stopped reading". Worse, the tally
   * index has a constant hash key and no sort key, which makes page order stable — so
   * the dropped tail is the SAME set of entries on every request, not a random
   * sample. Left unsignalled, a fixed group of entries would read as "no votes yet"
   * indefinitely while their votes existed.
   *
   * A client that sees `truncated` should render normally — the entries present are
   * accurate — but must NOT cache the response as authoritative.
   */
  truncated?: boolean;
}

// ── API: read the caller's own votes ───────────────────────

/**
 * GET /feedback/mine?installName={name}
 *
 * The caller's own vote on ONE entry, so the detail page can render "You marked
 * this useful" with its Undo affordance on a return visit and echo back the reasons
 * they gave. Whose vote is read comes from the verified token; `installName` only
 * selects which entry.
 *
 * This is what makes votes indefinitely mutable with no extra UI: the Undo
 * affordance a voter sees six months later is the same one the prototype shows six
 * seconds later.
 *
 * Deliberately per-entry rather than "all my votes"
 * ────────────────────────────────────────────────
 * A bulk version would need the voter's id as a QUERYABLE ATTRIBUTE (a GSI hash
 * key), which would put identity in the record body and break the anonymity
 * property above. Nothing needs the bulk form: cards show only the public tally,
 * the detail page concerns one entry, and #621 shows a contributor the votes on
 * THEIR entries, not a voter their own. So identity stays in the key alone.
 */
export interface MyVoteResponse {
  installName: string;

  /** The caller's vote, or null if they have not voted on this entry. */
  vote: FeedbackVote | null;
}

// ── API: cast or change a vote ─────────────────────────────

/**
 * POST /feedback
 *
 * Casts, or overwrites, the caller's vote on one entry. The vote and its reasons
 * commit TOGETHER in a single atomic write — #617's AC requires that "the count
 * and the contributor's copy of the reason can never get out of step", which is a
 * guarantee about the persistence layer, not just the UI. One transaction, so
 * there is no second write that can fail.
 *
 * Note what is NOT here: no voter identity (taken from the verified token) and no
 * counts (the server computes the delta from the prior state — a client that
 * could send "+1" could send "+1000").
 *
 * Server-side validation, all of which must reject rather than coerce:
 *   - installName is a non-empty, length-bounded string
 *   - every reasonCode is valid for `direction` (REASON_CODES_BY_DIRECTION)
 *   - no duplicate codes
 *   - reasonText length <= MAX_REASON_TEXT_LENGTH, measured before trimming
 *
 * installName EXISTENCE is deliberately not checked. The table cannot answer it
 * (plugin_store is keyed on pk/sk with only the two owner-email GSIs; manifestName
 * is a plain attribute and PluginStoreClient has no get-by-name), and consulting
 * the published marketplace.json on a write path would couple voting to catalog
 * publication and make a just-published entry unvotable. A vote against an
 * unmatched key is inert — never joined, never rendered. See feedback-request.ts.
 */
export interface CastVoteRequest {
  installName: string;
  direction: VoteDirection;

  /** May be empty — Submit with nothing selected is equivalent to Skip. */
  reasonCodes: ReasonCode[];

  /** Omit or null when the voter did not use "Other". */
  reasonText?: string | null;
}

/**
 * The entry's tally AFTER the write.
 *
 * Returned so the voter's own UI reflects their vote immediately rather than
 * waiting on the next bulk read — the piece that lets the bulk tallies be cached
 * without the actor ever seeing a stale number.
 */
export interface CastVoteResponse {
  installName: string;
  tally: FeedbackTally;
}

// ── API: withdraw a vote (Undo) ────────────────────────────

/**
 * DELETE /feedback
 *
 * Withdraws the caller's vote on one entry. Removes the vote AND its reasons in
 * the same transaction that decrements the tally — #617: "an Undo affordance
 * reverts both the vote and its reason together, never one without the other."
 *
 * Consequence worth stating plainly: a contributor can watch a piece of feedback
 * disappear from their dashboard. That is intended — a voter should not be able
 * to retract a vote while their criticism stays behind.
 *
 * Withdrawing a vote that does not exist is a no-op, not an error (idempotent
 * retry). No tombstone is kept: v1 has no vote history, and not retaining
 * retracted private feedback is the better privacy posture.
 */
export interface WithdrawVoteRequest {
  installName: string;
}

export type WithdrawVoteResponse = CastVoteResponse;

// ── Contributor view (#621) ────────────────────────────────

/**
 * One piece of feedback as shown to the CONTRIBUTOR who owns the entry.
 *
 * Anonymity is enforced by the type: there is no voter field to populate. The UI
 * promises "Your response is counted anonymously", so the read path must project
 * identity out — and since identity lives only in the storage key, the projection
 * is the default rather than something to remember.
 *
 * Strictly read-only. #621 requires that the prototype's reply capability
 * (startFeedbackReply / submitFeedbackReply) is fully removed, not hidden: no
 * replies, no threading, no moderation.
 */
export interface ContributorFeedbackItem {
  direction: VoteDirection;
  reasonCodes: ReasonCode[];
  reasonText: string | null;
  votedAt: string;
}

/**
 * Feedback on ONE entry, grouped so reasons stay attributed to the entry actually
 * voted on. A My Skills row (#621) sums the useful COUNT across a plugin and its
 * skills for its headline, but the written reasons are NEVER merged — each group
 * keeps its own `installName`, so a comment on one skill can't be misread as a
 * comment on the plugin. This is why the summed count does not reopen Contract 8's
 * no-rollup rule: the rule protects the public rating, this is a private aggregate.
 */
export interface ContributorFeedbackGroup {
  installName: string;
  items: ContributorFeedbackItem[];
}

/**
 * GET /feedback/received?installName={parent}
 *
 * All feedback across the caller's owned family for one entry — the plugin plus the
 * skills synthesized from its `skills[]`. Whose entries these are comes from the
 * verified token (the caller must own `installName`, else 404); `installName` only
 * selects which family.
 *
 * Read-only, and anonymous by type: `ContributorFeedbackItem` carries no voter field,
 * so there is no identity to project out. `groups` is SPARSE — entries with no
 * feedback are omitted, matching the absent-key-means-empty discipline of
 * `TalliesResponse`. `truncated` is present and `true` ONLY when `MAX_FAMILY_ENTRIES`
 * capped the fan-out; a client seeing it should render what arrived but not treat the
 * set as complete.
 */
export interface ReceivedFeedbackResponse {
  schemaVersion: "1.0";
  installName: string;
  groups: ContributorFeedbackGroup[];
  truncated?: boolean;
  /**
   * `true` ONLY when one or more fan-out legs failed or timed out (#621 review), so at
   * least one entry that MIGHT have had feedback is simply absent from `groups` rather
   * than genuinely empty. Distinct from `truncated`: truncation is an expected page
   * ceiling with a known cause, this is an unexpected per-entry failure with an unknown
   * one. A client seeing it should offer a retry rather than reading a short `groups`
   * (or none) as "no feedback left" for the dropped entry.
   */
  partial?: boolean;
}

/**
 * @example
 * // POST /feedback
 * const request: CastVoteRequest = {
 *   installName: "ghes--dx-team--readme-wizard",
 *   direction: "up",
 *   reasonCodes: ["SAVED_TIME", "FIT_MY_USE_CASE"],
 *   reasonText: null,
 * };
 *
 * // 200
 * const response: CastVoteResponse = {
 *   installName: "ghes--dx-team--readme-wizard",
 *   tally: { useful: 28, total: 34 },
 * };
 */

/**
 * Compile-time guard: the tally key is the catalog's own entry id, so a rename in
 * Contract 3 breaks the build here rather than silently producing a map keyed on
 * a field that no longer exists.
 */
export type FeedbackEntryKey = NonNullable<MarketplaceEntry["installName"]>;
